import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import ChatInput from "./components/ChatInput";
import ChatLog, { ChatLogHandle } from "./components/ChatLog";
import dummyData from "./dummy.json";
import { useGetBeforeChatLog, useGetChatAvailableCount, useGetFreeWhetherToUse, useGetStreamAIChatAnswer } from "../../api";
import { getUserSubs } from "../../utils/useGetSubsState";
import { WebAppBridge } from "@src/lib/WebAppBridge";

interface MessageData {
    role: "user" | "assistant";
    content: string;
}

interface TicketBoxState {
    TYPE: "HIDDEN" | "TICKET" | "UPGRADE" | "ONLY_UPGRADE";
}

interface OwnProps {
    onPaymentButtonClick?: (type?: "upgrade" | "ticket") => void;
}

const MissgoAIChatbot = ({ onPaymentButtonClick }: OwnProps) => {
    const searchParams = new URLSearchParams(location.search);
    const itemId = searchParams.get("itemId") ?? "";
    const userSubs = getUserSubs();

    const [chatData, setChatData] = useState<MessageData[]>(dummyData.data as MessageData[]);
    const [hasFirstStreamCompleted, setHasFirstStreamCompleted] = useState<boolean>(false);
    const [ticketBoxState, setTicketBoxState] = useState<TicketBoxState["TYPE"]>("HIDDEN");

    const { startStream, isLoading: isStreaming, cancelStream, error } = useGetStreamAIChatAnswer();
    const {
        data: beforeChatLogData,
        isLoading: beforeChatLogLoading,
        refetch: refetchBeforeChatLog,
    } = useGetBeforeChatLog(itemId, {
        enabled: !!itemId,
        onSuccess: (data) => {
            if (data?.data && chatData.length === (dummyData.data as MessageData[]).length) {
                setChatData([...(dummyData.data as MessageData[]), ...(data.data as MessageData[])]);
            }
        },
    });
    const { data: chatAvailableCount, refetch: refetchChatAvailableCount } = useGetChatAvailableCount();
    const { data: freeWhetherToUseData } = useGetFreeWhetherToUse();

    const chatLogRef = useRef<ChatLogHandle>(null);

    useEffect(() => {
        refetchBeforeChatLog();
        refetchChatAvailableCount();

        if (chatAvailableCount?.remainCount === 0 && chatAvailableCount?.ticketCount === 0 && userSubs === "VVIP") {
            setTicketBoxState("TICKET");
        }
        if (chatAvailableCount?.remainCount === 0 && chatAvailableCount?.ticketCount === 0 && userSubs === "VIP") {
            setTicketBoxState("UPGRADE");
        }
        if (
            chatAvailableCount?.remainCount === 0 &&
            (userSubs === "BASIC" || userSubs === "STANDARD" || userSubs === "NONE" || userSubs === "FREE_TRIAL")
        ) {
            setTicketBoxState("ONLY_UPGRADE");
        }
        if (chatAvailableCount?.remainCount! > 0 || chatAvailableCount?.ticketCount! > 0) {
            setTicketBoxState("HIDDEN");
        }
    }, [chatAvailableCount, userSubs]);

    const handleInputFocus = () => {
        chatLogRef.current?.scrollToBottom();
    };

    const handleSendMessage = (message: string) => {
        if (isStreaming) {
            return;
        }

        const userMessage: MessageData = { role: "user", content: message };
        const assistantPlaceholder: MessageData = { role: "assistant", content: "" };

        setChatData((prevData) => [...prevData, userMessage, assistantPlaceholder]);

        startStream(
            { itemId, question: message },
            {
                onChunk: (chunk) => {
                    setChatData((prevData) => {
                        const updatedData = [...prevData];
                        const lastMessageIndex = updatedData.length - 1;
                        if (lastMessageIndex >= 0 && updatedData[lastMessageIndex].role === "assistant") {
                            updatedData[lastMessageIndex].content += chunk;
                        }
                        return updatedData;
                    });
                },
                onDone: () => {
                    if (!hasFirstStreamCompleted) {
                        setHasFirstStreamCompleted(true);
                    }

                    setChatData((prevData) => {
                        const updatedData = [...prevData];
                        const lastMessageIndex = updatedData.length - 1;

                        if (lastMessageIndex < 0) return updatedData;

                        const finalContent = updatedData[lastMessageIndex].content;

                        if (finalContent.endsWith("\n")) {
                            updatedData[lastMessageIndex].content = finalContent.slice(0, -1);
                        }

                        return updatedData;
                    });

                    setHasFirstStreamCompleted(false);
                    refetchChatAvailableCount();
                },
                onError: (error) => {
                    console.log(error);
                    setChatData((prevData) => {
                        const updatedData = [...prevData];
                        const lastMessageIndex = updatedData.length - 1;
                        if (lastMessageIndex >= 0) {
                            updatedData[lastMessageIndex].content = "오류가 발생했습니다.";
                        }
                        return updatedData;
                    });

                    if (!hasFirstStreamCompleted) {
                        setHasFirstStreamCompleted(true);
                    }

                    refetchChatAvailableCount();
                },
            },
        );
    };

    const showLoading = beforeChatLogLoading || (isStreaming && !hasFirstStreamCompleted);

    function formatDate(expiredDay: string) {
        const date = new Date(expiredDay);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");

        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");

        return `${year}-${month}-${day} ${hours}:${minutes}`;
    }

    const ticketCount = () => {
        const count = chatAvailableCount?.ticketCount + chatAvailableCount?.remainCount;

        // VVIP 한달 300회 남았을때
        if (chatAvailableCount?.remainCount > 0 && chatAvailableCount?.ticketExpiredAt === null && userSubs === "VVIP") {
            return `${count}회/1달`;
        }

        // VVIP 한달 300회 다 쓰고 10회권 구매했을때
        if (chatAvailableCount?.remainCount > 0 && chatAvailableCount?.ticketExpiredAt === null && userSubs !== "VVIP") {
            return `${count}회/1일`;
        }

        // VIP 10회권 구매했을때
        if (chatAvailableCount?.ticketCount > 0 && chatAvailableCount?.ticketExpiredAt !== null) {
            return `${count}회/1일`;
        }

        // 티켓 다썼을때
        if (
            chatAvailableCount?.remainCount === 0 &&
            chatAvailableCount?.ticketCount === 0 &&
            chatAvailableCount?.ticketExpiredAt === null
        ) {
            return "0회";
        }
    };

    const expiredAt = () => {
        if (freeWhetherToUseData?.isFreeIssued && !freeWhetherToUseData?.isExpierd) {
            return <div>무료체험 만료일 : {freeWhetherToUseData?.expiredAt}</div>;
        }

        if (chatAvailableCount?.remainCount > 0 && chatAvailableCount?.ticketExpiredAt === null && userSubs === "VVIP") {
            return <div>갱신일 : </div>;
        }

        return <div>만료일 : {chatAvailableCount?.ticketExpiredAt ? formatDate(chatAvailableCount?.ticketExpiredAt) : "-"}</div>;
    };

    return (
        <MissgoAIChatbotContainer style={{ height: "calc(100vh - 140px)" }}>
            {/* <ChatRemainingCount>
                <div>남은 횟수 : {ticketCount()}</div>

                {expiredAt()}
            </ChatRemainingCount> */}

            <ChatLog ref={chatLogRef} data={chatData} isLoading={showLoading} />
            {/* ticketBoxState = HIDDEN, UPGRADE, TICKET, ONLY_UPGRADE */}

            {ticketBoxState !== "HIDDEN" && (
                <div className="sidePadding14">
                    <TicketWarningBox>
                        <div>티켓이 모두 소진되었습니다.</div>
                        <TicketButtonBox>
                            {ticketBoxState === "TICKET" && (
                                <button
                                    onClick={() => {
                                        onPaymentButtonClick?.("ticket");
                                    }}
                                >
                                    AI 챗봇 이용권 구매하기
                                </button>
                            )}
                            {ticketBoxState === "UPGRADE" && (
                                <>
                                    <button
                                        onClick={() => {
                                            onPaymentButtonClick?.("ticket");
                                        }}
                                    >
                                        AI 챗봇 이용권 구매하기
                                    </button>
                                    <button
                                        onClick={() => {
                                            onPaymentButtonClick?.("upgrade");
                                        }}
                                    >
                                        구독권 업그레이드
                                    </button>
                                </>
                            )}
                            {ticketBoxState === "ONLY_UPGRADE" && (
                                <button
                                    onClick={() => {
                                        onPaymentButtonClick?.("upgrade");
                                    }}
                                >
                                    구독권 업그레이드
                                </button>
                            )}
                        </TicketButtonBox>
                    </TicketWarningBox>
                </div>
            )}

            <ChatInputContainer>
                {ticketBoxState !== "HIDDEN" ? (
                    <ChatInputBox>
                        <ChatInput
                            onSubmit={handleSendMessage}
                            onCancel={cancelStream}
                            isLoading={isStreaming}
                            isDisabled={true}
                            onFocus={handleInputFocus}
                        />
                    </ChatInputBox>
                ) : (
                    <ChatInputBox>
                        <ChatInput
                            onSubmit={handleSendMessage}
                            onCancel={cancelStream}
                            isLoading={isStreaming}
                            onFocus={handleInputFocus}
                        />
                    </ChatInputBox>
                )}
            </ChatInputContainer>
        </MissgoAIChatbotContainer>
    );
};

const MissgoAIChatbotContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;

    gap: 12px;
`;

const ChatInputContainer = styled.div`
    width: calc(100% - 26px);
    box-sizing: border-box;
    position: fixed;
    bottom: 0;
    left: 14px;
    right: 14px;

    box-sizing: border-box;
    background-color: white;
`;

const ChatInputBox = styled.div`
    width: 100%;
`;

const ChatRemainingCount = styled.div`
    width: calc(100% - 8px);
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 30px;
    box-sizing: border-box;
    padding: 8px;
    text-align: center;

    border-radius: 4px;
    border: 1px solid #000;
    background: #fff;

    color: #000;
    font-size: 12px;
    font-weight: 400;
    line-height: 140%;
`;

const TicketWarningBox = styled.div`
    width: calc(100% - 8px);
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    box-sizing: border-box;

    border-radius: 5px;
    background: #000;

    color: #fff;
    font-size: 14px;
    font-weight: 400;
    line-height: 140%;
`;

const TicketButtonBox = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 8px;

    button {
        cursor: pointer;
        padding: 5px 12px;

        border-radius: 30px;
        background-color: #fff;

        color: #000;
        font-size: 10px;
        font-weight: 400;
        line-height: 140%;
    }
`;

export default MissgoAIChatbot;
